import { useAdminData } from "./AdminDataContext";
import { useAdminAuth } from "./AdminAuthContext";
import { Package, ShoppingCart, Calendar, BookOpen, AlertTriangle, TrendingUp, Star, CreditCard } from "lucide-react";
import { Link } from "react-router";

export function AdminDashboard() {
  const { products, orders, workshops, journal, reviews } = useAdminData();
  const { hasPermission } = useAdminAuth();

  const lowStockProducts = products.filter(
    (p) => p.stock_quantity <= p.low_stock_threshold && p.stock_quantity > 0
  );
  const outOfStockProducts = products.filter((p) => p.stock_quantity === 0);
  const pendingOrders = orders.filter((o) => o.status === "pending");
  const totalRevenue = orders
    .filter((o) => o.status !== "cancelled")
    .reduce((sum, o) => sum + o.subtotal, 0);

  const stats = [
    {
      label: "Products",
      value: products.length,
      icon: Package,
      color: "#6F7F63",
      link: "/admin/products",
      perm: "products",
    },
    {
      label: "Orders",
      value: orders.length,
      icon: ShoppingCart,
      color: "#C7A76C",
      link: "/admin/orders",
      perm: "orders",
      sub: pendingOrders.length > 0 ? `${pendingOrders.length} pending` : undefined,
    },
    {
      label: "Workshops",
      value: workshops.length,
      icon: Calendar,
      color: "#8A6D63",
      link: "/admin/workshops",
      perm: "workshops",
    },
    {
      label: "Journal",
      value: journal.length,
      icon: BookOpen,
      color: "#A7B39A",
      link: "/admin/journal",
      perm: "journal",
    },
  ];

  const pendingReviewCount = reviews.filter((r) => !r.approved).length;

  return (
    <div>
      <h1
        className="text-[#2D2A26] tracking-[0.08em] mb-8"
        style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}
      >
        Dashboard
      </h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
        {stats
          .filter((s) => hasPermission(s.perm as any))
          .map((stat) => (
            <Link
              key={stat.label}
              to={stat.link}
              className="bg-white rounded-xl p-5 shadow-[0_2px_16px_rgba(40,32,20,0.04)] hover:shadow-[0_4px_24px_rgba(40,32,20,0.08)] transition-all duration-300 group"
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ background: `${stat.color}15` }}
                >
                  <stat.icon size={20} style={{ color: stat.color }} strokeWidth={1.5} />
                </div>
                <span
                  className="text-[#2D2A26]"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "28px", fontWeight: 400 }}
                >
                  {stat.value}
                </span>
              </div>
              <p className="text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "11px" }}>
                {stat.label}
              </p>
              {stat.sub && (
                <p className="text-[#C7A76C] mt-1" style={{ fontSize: "11px" }}>
                  {stat.sub}
                </p>
              )}
            </Link>
          ))}
      </div>

      {/* Revenue */}
      {hasPermission("orders") && orders.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)] mb-8">
          <div className="flex items-center gap-3 mb-2">
            <TrendingUp size={18} className="text-[#6F7F63]" strokeWidth={1.5} />
            <p className="text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "11px" }}>
              Total Revenue
            </p>
          </div>
          <p
            className="text-[#2D2A26]"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "36px", fontWeight: 400 }}
          >
            &euro;{totalRevenue.toLocaleString()}
          </p>
        </div>
      )}

      {/* Alerts */}
      {hasPermission("products") && (lowStockProducts.length > 0 || outOfStockProducts.length > 0) && (
        <div className="bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)]">
          <div className="flex items-center gap-3 mb-5">
            <AlertTriangle size={18} className="text-[#8B5C58]" strokeWidth={1.5} />
            <p className="text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "11px" }}>
              Stock Alerts
            </p>
          </div>
          <div className="space-y-3">
            {outOfStockProducts.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2 border-b border-[#E6E0D7] last:border-0">
                <div>
                  <p className="text-[#2D2A26]" style={{ fontSize: "14px" }}>{p.name}</p>
                  <p className="text-[#8B5C58]" style={{ fontSize: "11px" }}>Out of Stock</p>
                </div>
                <span
                  className="px-2.5 py-1 bg-[#8B5C58]/10 text-[#8B5C58] rounded-full"
                  style={{ fontSize: "11px" }}
                >
                  0 units
                </span>
              </div>
            ))}
            {lowStockProducts.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2 border-b border-[#E6E0D7] last:border-0">
                <div>
                  <p className="text-[#2D2A26]" style={{ fontSize: "14px" }}>{p.name}</p>
                  <p className="text-[#C7A76C]" style={{ fontSize: "11px" }}>Low Stock</p>
                </div>
                <span
                  className="px-2.5 py-1 bg-[#C7A76C]/10 text-[#C7A76C] rounded-full"
                  style={{ fontSize: "11px" }}
                >
                  {p.stock_quantity} units
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Reviews */}
      {hasPermission("site_config") && pendingReviewCount > 0 && (
        <Link
          to="/admin/site-config"
          className="block mt-6 bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)] hover:shadow-[0_4px_24px_rgba(40,32,20,0.08)] transition-all"
        >
          <div className="flex items-center gap-3">
            <Star size={18} className="text-[#C7A76C]" strokeWidth={1.5} />
            <p className="text-[#6F675F] tracking-[0.1em] uppercase" style={{ fontSize: "11px" }}>
              Pending Reviews
            </p>
            <span className="px-2.5 py-0.5 bg-[#C7A76C]/15 text-[#C7A76C] rounded-full" style={{ fontSize: "11px" }}>
              {pendingReviewCount} awaiting approval
            </span>
          </div>
        </Link>
      )}

      {/* Stripe Readiness */}
      {hasPermission("site_config") && (
        <Link
          to="/admin/payments"
          className="block mt-6 bg-white rounded-xl p-6 shadow-[0_2px_16px_rgba(40,32,20,0.04)] hover:shadow-[0_4px_24px_rgba(40,32,20,0.08)] transition-all"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#635BFF]/10 flex items-center justify-center">
                <CreditCard size={16} className="text-[#635BFF]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-[#2D2A26]" style={{ fontSize: "13px", fontWeight: 500 }}>
                  Payments & Stripe
                </p>
                <p className="text-[#6F675F]" style={{ fontSize: "11px", fontWeight: 300 }}>
                  Database ready &middot; Integration pending
                </p>
              </div>
            </div>
            <span
              className="px-2.5 py-1 bg-[#C7A76C]/10 text-[#C7A76C] rounded-full"
              style={{ fontSize: "10px", letterSpacing: "0.08em" }}
            >
              ORDER REQUEST MODE
            </span>
          </div>
        </Link>
      )}
    </div>
  );
}